/**
 * MealSlot Component
 *
 * Displays a meal slot within the weekly calendar grid.
 * Shows assigned recipe with servings or an empty state.
 * Opens RecipePicker modal on click for recipe assignment.
 * Allows portion adjustment via servings input.
 * Supports drag-and-drop for moving recipes between slots.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { MealSlot as MealSlotType, MealType, DayOfWeek } from '../../types/mealPlan';
import { useMealPlanStore } from '../../stores/mealPlanStore';
import './mealPlan.css';

interface MealSlotProps {
  slot: MealSlotType | null;
  day: string;
  dayIndex: DayOfWeek;
  mealType: MealType;
  onSlotClick: (dayIndex: DayOfWeek, mealType: MealType, slot: MealSlotType | null) => void;
}

interface DragData {
  slotId: number;
  recipeId: number;
  servings: number;
  dayIndex: DayOfWeek;
  mealType: MealType;
}

export function MealSlot({ slot, day, dayIndex, mealType, onSlotClick }: MealSlotProps) {
  const { t } = useTranslation('mealPlan');
  const navigate = useNavigate();
  const { updateMealSlot, assignRecipeToSlot, removeMealSlot, currentMealPlan } = useMealPlanStore();
  const [servings, setServings] = useState<number>(slot?.servings || 1);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Update local servings state when slot changes
  useEffect(() => {
    if (slot?.servings) {
      setServings(slot.servings);
    }
  }, [slot?.servings]);

  const handleClick = () => {
    onSlotClick(dayIndex, mealType, slot);
  };

  const handleServingsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newServings = parseInt(e.target.value, 10);

    // Validate servings is a positive number
    if (isNaN(newServings) || newServings < 1) {
      return;
    }

    setServings(newServings);

    // Only update if we have a slot and meal plan
    if (slot && currentMealPlan) {
      setIsUpdating(true);
      try {
        await updateMealSlot(currentMealPlan.id, slot.id, { servings: newServings });
      } catch (error) {
        // Revert to previous servings on error
        setServings(slot.servings || 1);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent opening the RecipePicker modal
    e.stopPropagation();
  };

  const handleViewRecipe = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (slot?.recipe_id) {
      navigate(`/recipes/${slot.recipe_id}`);
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent) => {
    if (!slot?.recipe || !slot.recipe_id) return;

    setIsDragging(true);

    const dragData: DragData = {
      slotId: slot.id,
      recipeId: slot.recipe_id,
      servings: slot.servings || 1,
      dayIndex,
      mealType,
    };

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!currentMealPlan) return;

    try {
      const dragDataStr = e.dataTransfer.getData('application/json');
      if (!dragDataStr) return;

      const dragData: DragData = JSON.parse(dragDataStr);

      // Don't drop on the same slot
      if (
        dragData.dayIndex === dayIndex &&
        dragData.mealType === mealType
      ) {
        return;
      }

      setIsUpdating(true);

      if (slot?.recipe_id) {
        // Swap: Both slots have recipes
        // Update dragged slot with target slot's recipe
        await updateMealSlot(currentMealPlan.id, dragData.slotId, {
          recipe_id: slot.recipe_id,
          servings: slot.servings,
        });

        // Update target slot with dragged slot's recipe
        await updateMealSlot(currentMealPlan.id, slot.id, {
          recipe_id: dragData.recipeId,
          servings: dragData.servings,
        });
      } else {
        // Move: Target slot is empty
        // Remove recipe from source slot
        await removeMealSlot(currentMealPlan.id, dragData.slotId);

        // Assign recipe to target slot
        await assignRecipeToSlot(
          currentMealPlan.id,
          dayIndex,
          mealType,
          dragData.recipeId,
          dragData.servings
        );
      }
    } catch (error) {
      // Error is handled by store
    } finally {
      setIsUpdating(false);
    }
  };

  // Build className with drag states
  const slotClassName = `meal-slot${isDragging ? ' dragging' : ''}${isDragOver ? ' drag-over' : ''}`;

  return (
    <div
      className={slotClassName}
      data-day={day}
      data-meal={mealType}
      draggable={!!slot?.recipe}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`${day} ${mealType} meal slot${slot?.recipe ? `: ${slot.recipe.title}` : ''}`}
    >
      {slot?.recipe ? (
        <div className="meal-slot-content">
          <div className="recipe-name-row">
            <span className="recipe-name">{slot.recipe.title}</span>
            <button
              className="recipe-link-button"
              onClick={handleViewRecipe}
              aria-label={`View recipe: ${slot.recipe.title}`}
              title={t('viewRecipe')}
            >
              ↗
            </button>
          </div>
          <div className="servings-adjustment" onClick={handleInputClick}>
            <label htmlFor={`servings-${slot.id}`} className="servings-label-inline">
              {t('servingsLabel')}
            </label>
            <input
              id={`servings-${slot.id}`}
              type="number"
              min="1"
              value={servings}
              onChange={handleServingsChange}
              onClick={handleInputClick}
              onFocus={handleInputFocus}
              className="servings-input-inline"
              disabled={isUpdating}
              aria-label={`Servings for ${slot.recipe.title}`}
            />
          </div>
        </div>
      ) : (
        <div className="meal-slot-empty">
          <span className="empty-text">+</span>
        </div>
      )}
    </div>
  );
}

export default MealSlot;
